// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { ObservableTypeInput, ObservableTypeOutput } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/';
import {
    ObservableTypeValuesForm,
    ObservableTypeForm,
} from '@Organisms/Observable/ObservableTypeManage/ObservableTypeForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';

interface ObservableTypeManageProps {
    data?: ObservableTypeOutput;
    triggerElem?: ReactElement;
    onFinish: (ObservableType: ObservableTypeInput) => void;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

export const ObservableTypeManage = ({
    data,
    triggerElem,
    onFinish,
    visible,
    onVisibleChange,
}: ObservableTypeManageProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinishHandler = ({ name = '', isAttachment = false }: ObservableTypeValuesForm): void => {
        onFinish({ name: name.trim(), isAttachment });
    };

    const tradLabelKey = !!data ? 'edit' : 'create';

    return (
        <THDrawer
            title={i(`observable-type.${tradLabelKey}.drawer.title`)}
            triggerElem={triggerElem}
            afterVisibleChange={(visible) => {
                form.resetFields();
                onVisibleChange?.(visible);
            }}
            className="drawer-form"
            disableClose={fieldsTouched}
            visible={visible}
        >
            {(toggleDrawer) => (
                <ObservableTypeForm
                    onCancel={() => {
                        form.resetFields();
                        setFieldsTouched(false);
                        toggleDrawer();
                    }}
                    onFinish={(values) => {
                        onFinishHandler(values);
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                    form={form}
                    isValid={isValid && fieldsTouched}
                    defaultValues={data}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                />
            )}
        </THDrawer>
    );
};
